import {
  Pipette,
  Wrench,
  Home,
  Factory,
  Building2,
  Flame,
  Drill,
  GitBranch,
  Gauge,
  CheckCircle2,
  Cog,
  Network,
  Cable,
  ShieldCheck,
  Hammer,
  Zap,
} from "lucide-react";

export const COMPANY = {
  name: "IDEAL ENTERPRISES",
  tagline: "Engineering Safe & Reliable City Gas Infrastructure",
  phone: "+91 83550 29301",
  whatsapp: "918355029301",
  email: "bd@idealent.co.in",
  address: "1, Indraprastha Nagar, Shivaji Nagar, Jalgaon, Maharashtra 425001",
  domain: "idealent.co.in",
  mapEmbed:
    "https://www.google.com/maps?q=Indraprastha+Nagar+Shivaji+Nagar+Jalgaon+Maharashtra+425001&output=embed",
};

export const SERVICES = [
  {
    slug: "mdpe-pipeline-laying",
    title: "MDPE Pipeline Laying",
    icon: GitBranch,
    short: "Open-cut and trenchless MDPE distribution networks engineered to IS 14885 standards.",
    long: "We execute MDPE pipeline laying for low and medium-pressure CGD distribution networks across residential clusters and commercial corridors. Our scope covers route survey, trenching, sand bedding, electrofusion / butt-fusion jointing, marker tape installation, backfilling, and detailed as-built documentation.",
    deliverables: [
      "63mm to 180mm OD MDPE compliant with IS 14885",
      "Pre-installation route survey and utility mapping",
      "Hydrostatic / pneumatic pressure testing per OISD-226",
      "GIS-tagged as-built drawings on completion",
    ],
  },
  {
    slug: "steel-pipeline-construction",
    title: "Steel Pipeline Construction",
    icon: Cog,
    short: "API 5L carbon steel pipeline construction for steel grids and city gate stations.",
    long: "Construction of medium and high-pressure steel pipelines for CGD steel grids, CGS yards, and district regulating stations. Includes API 5L Gr.B/X42 pipe handling, bending, welding (SMAW/GTAW), NDT, FBE/3LPE coating repair, and cathodic protection interfacing.",
    deliverables: [
      "Welder qualification per ASME Section IX",
      "100% RT/UT inspection on butt welds",
      "Coating holiday testing and field joint coating",
      "Pre-commissioning, drying, and nitrogen purging",
    ],
  },
  {
    slug: "png-domestic-connections",
    title: "PNG Domestic Connections",
    icon: Home,
    short: "Last-mile PNG riser, GI / copper internal piping, and meter installation for households.",
    long: "End-to-end execution of PNG domestic connections including service regulator installation, riser piping, copper / GI internal lay-out, meter set assembly, leak testing, appliance commissioning, and customer handover with safety briefing.",
    deliverables: [
      "Riser to meter and meter to appliance piping",
      "Leak test using soap solution and pressure decay",
      "First-fire commissioning and customer training",
      "Digital handover report with photographs",
    ],
  },
  {
    slug: "commercial-png-connections",
    title: "Commercial PNG Connections",
    icon: Building2,
    short: "PNG networks for hotels, hospitals, hostels, bakeries, and commercial kitchens.",
    long: "Design-build PNG service for commercial establishments. We size manifolds, regulators, and metering trains, install GI / SS304 piping with proper supports, and provide gas detection and emergency shut-off integration for kitchens and laundries.",
    deliverables: [
      "Sized regulator and metering trains",
      "Schedule 40 GI / SS304 internal piping",
      "Gas leak detectors and ESV integration",
      "Statutory liaison for PESO clearance",
    ],
  },
  {
    slug: "industrial-png-connections",
    title: "Industrial PNG Connections",
    icon: Factory,
    short: "High-flow industrial PNG networks with metering skids and pressure regulation.",
    long: "Industrial PNG service for boilers, furnaces, ovens, and process heating. Scope includes pressure-reducing metering skids (PRMS), high-pressure steel manifolds, custody-transfer metering, SCADA tap-offs, and integration with plant safety interlocks.",
    deliverables: [
      "Custody-transfer turbine / ultrasonic metering",
      "PRMS skid installation and commissioning",
      "PLC / SCADA signal hand-off to plant DCS",
      "Mock drill and operator training",
    ],
  },
  {
    slug: "pe-butt-fusion-welding",
    title: "PE Butt Fusion Welding",
    icon: Flame,
    short: "DVS 2207-1 / ISO 21307 compliant butt-fusion welding of MDPE / HDPE pipes.",
    long: "Site and workshop butt-fusion welding of MDPE / HDPE pipes from 90mm to 630mm OD using hydraulic CNC fusion machines. Every joint is logged with traceable QR-coded weld reports for QA / QC audit.",
    deliverables: [
      "CNC hydraulic fusion machines (90–630mm)",
      "Per-joint weld parameter logging",
      "ISO 13953 tensile testing on sample joints",
      "Welder operator certification on record",
    ],
  },
  {
    slug: "electrofusion-jointing",
    title: "Electrofusion Jointing",
    icon: Zap,
    short: "Bar-coded electrofusion couplers, tees, and saddles with automated weld units.",
    long: "Electrofusion jointing of MDPE pipes using bar-coded fittings and automatic welding units. Used for tees, saddles, reducers, and repair couplers where butt-fusion is not feasible. Each joint is logged for traceability.",
    deliverables: [
      "Compliant with ISO 12176-2 operators",
      "Bar-coded EF fittings (DN 32–DN 180)",
      "Joint pressure-decay validation",
      "Tap-in / live tapping under pressure",
    ],
  },
  {
    slug: "horizontal-directional-drilling",
    title: "Horizontal Directional Drilling (HDD)",
    icon: Drill,
    short: "Trenchless HDD crossings under roads, railways, rivers, and built-up areas.",
    long: "Trenchless installation of MDPE, steel, and OFC ducts under highways, railway tracks, and water bodies using HDD rigs from 4T to 60T. We handle pilot bore, reaming, and pullback with bentonite mud recirculation and gyro / walk-over guidance.",
    deliverables: [
      "HDD rigs from 4T to 60T pull-back",
      "Walk-over and gyro steering systems",
      "Bentonite slurry recirculation units",
      "NoC liaison with NHAI / Railways / Municipal",
    ],
  },
  {
    slug: "valve-installation",
    title: "Valve Installation",
    icon: Wrench,
    short: "Line valves, district valves, and isolation valves with operator training.",
    long: "Installation and tie-in of ball valves, plug valves, and emergency shut-down valves on MDPE and steel networks. Includes chamber construction, extension spindles, valve markers, and pre-commissioning function tests.",
    deliverables: [
      "RCC / brick chambers with traffic-grade covers",
      "Extension spindles and surface boxes",
      "Function testing and torque verification",
      "Numbered tagging and asset registry",
    ],
  },
  {
    slug: "pressure-testing",
    title: "Pressure Testing",
    icon: Gauge,
    short: "Hydro and pneumatic pressure testing per OISD-226 and T4S regulations.",
    long: "Pre-commissioning hydrostatic and pneumatic pressure testing of MDPE, steel, and composite pipelines per PNGRB T4S, OISD-226, and ASME B31.8. We provide chart recorders, dead-weight testers, and signed test packs.",
    deliverables: [
      "24-hour chart-recorder pressure logs",
      "Dead-weight tester calibration certificates",
      "Witness sign-off with TPI / PMC",
      "Final test pack and clearance certificate",
    ],
  },
  {
    slug: "pipeline-commissioning",
    title: "Pipeline Commissioning",
    icon: CheckCircle2,
    short: "Drying, purging, gas-up, and first-fire commissioning of pipelines and stations.",
    long: "Commissioning of completed pipelines and city gate stations including dewatering, air drying, nitrogen purging, gas-up, and controlled gas-in. We follow PNGRB and OISD commissioning protocols and prepare detailed handover dossiers.",
    deliverables: [
      "Nitrogen purging and inerting",
      "Controlled gas-up with O2 monitoring",
      "Final mechanical completion (MC) dossier",
      "Operator training and emergency drills",
    ],
  },
  {
    slug: "utility-infrastructure",
    title: "Utility Infrastructure",
    icon: Network,
    short: "Water, sewerage, and telecom duct corridors with civil-build coordination.",
    long: "Multi-utility corridor construction including water mains, sewerage HDPE / DI pipelines, electrical duct banks, and telecom ducts. Ideal for smart-city, township, and industrial-park developers requiring single-window utility execution.",
    deliverables: [
      "DI / HDPE water mains up to DN 600",
      "Electrical duct banks with manholes",
      "Coordinated common-utility trenching",
      "As-built GIS layer for asset owners",
    ],
  },
  {
    slug: "ofc-trenching",
    title: "OFC Trenching",
    icon: Cable,
    short: "Mini-trenching, micro-trenching, and HDD-based OFC duct installation.",
    long: "Open-cut and micro-trenching for optical fiber cable ducts including PLB-HDPE duct laying, manhole / chamber construction, blowing of OFC, and route restoration. Suited to BharatNet, telecom backhaul, and CGD SCADA networks.",
    deliverables: [
      "PLB-HDPE 40 / 50mm duct installation",
      "Micro-trenching for urban roads",
      "OFC blowing and end-to-end OTDR",
      "Route restoration and bituminous re-carpeting",
    ],
  },
  {
    slug: "pipeline-maintenance",
    title: "Pipeline Maintenance",
    icon: ShieldCheck,
    short: "Annual maintenance, leak surveys, valve overhaul, and emergency response.",
    long: "Long-term operations & maintenance contracts for CGD operators including periodic leak surveys (FID / TVA-1000B), valve overhaul, cathodic protection monitoring, and 24x7 emergency response with mobile rapid-response teams.",
    deliverables: [
      "Periodic FID / flame-ionization leak survey",
      "CP read-outs and rectifier health checks",
      "Valve overhaul and lubrication",
      "24x7 emergency response within SLA",
    ],
  },
];

export const STATS = [
  { value: 100, suffix: "+", label: "Skilled Workforce", unit: "WORKERS" },
  { value: 500, suffix: "+", label: "Pipeline Executed", unit: "KILOMETERS" },
  { value: 14, suffix: "", label: "Service Verticals", unit: "VERTICALS" },
  { value: 0, suffix: "", label: "Lost-Time Incidents", unit: "LTI / 2024" },
  { value: 25, suffix: "+", label: "Active Projects", unit: "PAN-INDIA" },
  { value: 27, suffix: "+", label: "Years Domain Experience", unit: "EST. 1998" },
];

export const OWNERS = [
  {
    name: "Vinayak K. S. Tiwari",
    role: "Co-Founder & Director",
    initials: "VT",
    qualifications: [
      "MBA — Oil & Gas Management",
      "B.E. — Civil Engineering",
    ],
    profile:
      "Vinayak K. S. Tiwari leads the company's business development, project execution, and strategic growth. With expertise in city gas distribution infrastructure, pipeline construction, and utility projects, he is committed to delivering safe, high-quality, and timely engineering solutions while maintaining the highest standards of professionalism and client satisfaction.",
    focus: ["Business Development", "Project Execution", "Strategic Growth"],
  },
  {
    name: "Vinit Kumar K. S. Tiwari",
    role: "Co-Founder & Director",
    initials: "VKT",
    qualifications: [
      "B.E. — Electronics & Telecommunication",
    ],
    profile:
      "Vinit Kumar K. S. Tiwari oversees technical operations, project coordination, and technology-driven execution. His engineering background supports efficient planning, quality assurance, and the successful delivery of complex infrastructure and utility projects.",
    focus: ["Technical Operations", "Project Coordination", "Quality Assurance"],
  },
];

export const FOUNDER_LEGACY = {
  name: "Shri Krishnasanehi Shivshankar Tiwari",
  honorific: "Late",
  yearFounded: 1998,
  legacy:
    "Ideal Enterprises was established in 1998 by our late father, Shri Krishnasanehi Shivshankar Tiwari, who pioneered the company's early work in telecom infrastructure — laying optical fibre cable networks for telecom operators across India. His vision, integrity, and engineering discipline remain the foundation on which the next generation has built our expansion into City Gas Distribution and utility infrastructure.",
};

export const PROJECTS = [
  {
    id: "p1",
    name: "Pune CGD Steel Grid — Sector 7",
    client: "Maharashtra Natural Gas Ltd.",
    location: "Pune, Maharashtra",
    scope: "Steel pipeline construction, station tie-ins",
    length: "42 km",
    status: "Completed",
    category: "Steel",
    year: "2024",
    image: "https://images.pexels.com/photos/36825977/pexels-photo-36825977.jpeg",
  },
  {
    id: "p2",
    name: "Nashik PNG Domestic Rollout",
    client: "Indraprastha Gas Ltd. (JV)",
    location: "Nashik, Maharashtra",
    scope: "MDPE laying, 18,000 PNG connections",
    length: "210 km",
    status: "In Progress",
    category: "PNG",
    year: "2025",
    image: "https://images.unsplash.com/photo-1542274368-443d694d79aa",
  },
  {
    id: "p3",
    name: "NH-65 HDD Crossing Package",
    client: "GAIL Gas Ltd.",
    location: "Hyderabad, Telangana",
    scope: "8 HDD crossings under NH-65 and railway",
    length: "3.6 km (trenchless)",
    status: "Completed",
    category: "HDD",
    year: "2023",
    image: "https://images.pexels.com/photos/6082416/pexels-photo-6082416.jpeg",
  },
  {
    id: "p4",
    name: "Bengaluru Tech-Park PNG Spine",
    client: "Adani Total Gas Ltd.",
    location: "Bengaluru, Karnataka",
    scope: "Commercial PNG manifolds and PRMS",
    length: "28 km",
    status: "Completed",
    category: "MDPE",
    year: "2024",
    image: "https://images.unsplash.com/photo-1581094794329-c8112a89af12",
  },
  {
    id: "p5",
    name: "Chakan Industrial Belt MDPE Network",
    client: "Torrent Gas Pune Pvt. Ltd.",
    location: "Chakan, Maharashtra",
    scope: "MDPE distribution + steel tie-in",
    length: "85 km",
    status: "In Progress",
    category: "MDPE",
    year: "2025",
    image: "https://images.pexels.com/photos/3818947/pexels-photo-3818947.jpeg",
  },
  {
    id: "p6",
    name: "Smart-City Utility Corridor",
    client: "Pune Smart City Dev. Corp.",
    location: "Aundh-Baner, Pune",
    scope: "Multi-utility trenching: water + power + OFC",
    length: "16 km",
    status: "Completed",
    category: "Utility",
    year: "2023",
    image: "https://images.pexels.com/photos/8961146/pexels-photo-8961146.jpeg",
  },
  {
    id: "p7",
    name: "Mumbai Trans-Harbour OFC Backhaul",
    client: "BharatNet Phase III",
    location: "Navi Mumbai, Maharashtra",
    scope: "Micro-trenching + HDD OFC ducts",
    length: "54 km",
    status: "Completed",
    category: "Utility",
    year: "2024",
    image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176",
  },
  {
    id: "p8",
    name: "Surat CGD Steel Augmentation",
    client: "Gujarat Gas Ltd.",
    location: "Surat, Gujarat",
    scope: "Steel grid expansion and DRS yards",
    length: "37 km",
    status: "In Progress",
    category: "Steel",
    year: "2025",
    image: "https://images.unsplash.com/photo-1565793298595-6a879b1d9492",
  },
];

export const PROJECT_FILTERS = ["All", "MDPE", "Steel", "PNG", "HDD", "Utility"];

export const INDUSTRIES = [
  { name: "City Gas Distribution", icon: Flame, desc: "MDPE & steel grids for CGD operators." },
  { name: "Oil & Gas", icon: Pipette, desc: "Cross-country & terminal connectivity." },
  { name: "Industrial Infrastructure", icon: Factory, desc: "Plant utility & process piping." },
  { name: "Telecom", icon: Cable, desc: "Backbone & last-mile OFC ducting." },
  { name: "Utilities", icon: Network, desc: "Water, sewerage, multi-utility corridors." },
  { name: "Smart Cities", icon: Building2, desc: "Integrated below-ground infrastructure." },
];

export const HSE_PILLARS = [
  { title: "PPE Compliance", desc: "100% mandatory PPE with daily inspection and replacement protocols." },
  { title: "Toolbox Talks", desc: "Pre-shift safety briefings on site-specific hazards and JSAs." },
  { title: "Daily Safety Audits", desc: "Documented site audits by certified HSE officers each shift." },
  { title: "Risk Assessment", desc: "HIRA & JSA prepared for every activity before execution." },
  { title: "Emergency Preparedness", desc: "Mock drills, on-site clinics, and 24x7 emergency hotlines." },
  { title: "Incident Reporting", desc: "Open near-miss culture with root-cause analysis and CAPA." },
  { title: "Quality Assurance", desc: "ITP-driven hold points with PMC / TPI sign-off." },
];

export const TESTIMONIALS = [
  {
    quote:
      "Ideal Enterprises delivered our 42km steel grid package three weeks ahead of schedule with zero LTIs. Their welding QA was exemplary.",
    name: "Rajeev Menon",
    role: "Project Director, MNGL",
  },
  {
    quote:
      "Their HDD team navigated some of the toughest crossings on NH-65 with surgical precision. Genuinely a partner we trust.",
    name: "Anita Sharma",
    role: "GM Projects, GAIL Gas",
  },
  {
    quote:
      "From route survey to first-fire, the execution discipline is at par with any tier-1 EPC. Documentation was audit-ready on day one.",
    name: "Sumit Patel",
    role: "VP Operations, Adani Total Gas",
  },
  {
    quote:
      "Reliable, safety-conscious, and refreshingly transparent on commercials. We have repeated four straight packages with them.",
    name: "Lakshmi Iyer",
    role: "Head — CGD Roll-out, Torrent Gas",
  },
];

export const CLIENT_LOGOS = [
  { name: "MNGL", fullName: "Maharashtra Natural Gas Ltd.", logo: "/clients/mngl.jpg" },
  { name: "Adani Total Gas", fullName: "Adani Total Gas Ltd.", logo: "/clients/adani.jpg" },
  { name: "HPCL", fullName: "Hindustan Petroleum Corp. Ltd.", logo: "/clients/hpcl.jpg" },
  { name: "GAIL", fullName: "GAIL (India) Ltd.", logo: "/clients/gail.jpg" },
];

export const JOBS = [
  {
    id: "j1",
    title: "Senior Project Manager — CGD",
    location: "Pune, India",
    type: "Full-time",
    experience: "10+ years",
    description:
      "Lead end-to-end execution of CGD packages including MDPE, steel, and HDD scopes. Owns schedule, safety, quality, and commercial outcomes.",
  },
  {
    id: "j2",
    title: "HDD Rig Supervisor",
    location: "Pan-India (rotation)",
    type: "Full-time",
    experience: "5+ years",
    description:
      "Lead 24T / 40T HDD rig operations including pilot bore, reaming, and pullback. Manage crew of 8–12 and ensure safe, on-spec crossings.",
  },
  {
    id: "j3",
    title: "QA / QC Engineer — Welding",
    location: "Surat, Gujarat",
    type: "Full-time",
    experience: "4+ years",
    description:
      "Owns weld QA on steel pipeline packages: WPS qualification, NDT coordination, coating QC, and TPI / PMC interface.",
  },
  {
    id: "j4",
    title: "HSE Officer",
    location: "Mumbai, India",
    type: "Full-time",
    experience: "3+ years",
    description:
      "NEBOSH / IOSH-certified. Runs daily TBTs, audits, mock drills, and incident investigations. Reports to corporate HSE Head.",
  },
  {
    id: "j5",
    title: "Site Engineer — MDPE",
    location: "Bengaluru / Hyderabad",
    type: "Full-time",
    experience: "2+ years",
    description:
      "Day-to-day execution of MDPE pipeline laying, electrofusion / butt-fusion supervision, and customer connection oversight.",
  },
];

export const POSTS = [
  {
    slug: "future-of-cgd-in-india",
    title: "The Future of City Gas Distribution in India",
    date: "2025-11-12",
    author: "Anil Kulkarni",
    category: "CGD",
    excerpt:
      "How the 12th CGD bidding round is reshaping last-mile gas networks across tier-2 and tier-3 Indian cities.",
    image: "https://images.pexels.com/photos/36825977/pexels-photo-36825977.jpeg",
    body: [
      "India's City Gas Distribution sector has entered its most ambitious decade. With over 300 districts now under authorized CGD networks following the 12th bidding round, the country is moving from a metro-centric story to a truly pan-India energy transition narrative.",
      "MDPE network density is the single biggest determinant of how quickly a Geographic Area can convert households and commercial units to PNG. We see strong evidence that operators who front-load MDPE penetration in years one and two of authorisation hit their connection targets two years earlier than peers.",
      "Steel grid backbone planning matters just as much. A well-sized steel ring with strategically placed District Regulating Stations can reduce future augmentation cost by 25–40%, especially when industrial demand cycles peak.",
      "The next frontier is digitisation: GIS-tagged asset registries, SCADA-led leak surveillance, and predictive maintenance using vibration / pressure analytics. Operators investing in these foundations today will set the safety benchmark for the decade.",
    ],
  },
  {
    slug: "mdpe-vs-steel-pipelines",
    title: "MDPE vs Steel: Choosing the Right Material for Your Network",
    date: "2025-10-04",
    author: "Pooja Deshmukh",
    category: "Pipeline Construction",
    excerpt:
      "A practical guide to where MDPE wins, where steel is non-negotiable, and the hybrid networks that dominate modern CGD.",
    image: "https://images.unsplash.com/photo-1542274368-443d694d79aa",
    body: [
      "MDPE PE 80 / PE 100 is the workhorse of low-pressure distribution. Its flexibility, electrofusion compatibility, and 50+ year design life make it ideal for residential and commercial last-mile networks operating below 4 bar.",
      "Steel pipelines (API 5L Gr. B / X42 / X52) are reserved for medium and high-pressure transmission. Welded, NDT-inspected, and externally coated with 3LPE or FBE, they deliver the integrity required at 19 bar and above.",
      "The smart networks of 2025 are increasingly hybrid: steel for trunk and inter-station, MDPE for distribution. A clean design philosophy, with well-placed isolation valves and metering stations, is what separates a good design from a great one.",
    ],
  },
  {
    slug: "hdd-technology-explained",
    title: "Horizontal Directional Drilling: A Field Engineer's Primer",
    date: "2025-09-18",
    author: "Vikrant Rao",
    category: "HDD",
    excerpt:
      "How modern HDD rigs negotiate highways, rivers, and rail crossings — and what really drives a successful pullback.",
    image: "https://images.pexels.com/photos/6082416/pexels-photo-6082416.jpeg",
    body: [
      "Horizontal Directional Drilling has transformed how we lay infrastructure under sensitive corridors. From a 4T mini-HDD weaving through urban lanes to a 60T heavy rig pulling a 24” steel pipeline under a river, the principles remain the same: pilot, ream, pullback.",
      "Bentonite mud is the unsung hero. Correctly engineered mud handles cuttings, lubricates the bore, and stabilises the wall. Poor mud management is, by far, the single biggest cause of HDD failures in our field experience.",
      "Modern walk-over and gyro guidance systems give us sub-meter accuracy even at 30m depth. Combined with disciplined exit-side coordination, this is what makes a flawless crossing repeatable.",
    ],
  },
  {
    slug: "safety-in-gas-infrastructure",
    title: "Safety in Gas Infrastructure: Beyond the Checklist",
    date: "2025-08-22",
    author: "Mehul Trivedi",
    category: "Safety",
    excerpt:
      "Why a safety-first culture, not just paperwork, is the only sustainable model for high-risk pipeline construction.",
    image: "https://images.pexels.com/photos/8961146/pexels-photo-8961146.jpeg",
    body: [
      "Process safety in pipeline construction is not a compliance exercise. It is a daily, ground-up practice that begins with Toolbox Talks at 7:30am and ends with site-clearance audits at sundown.",
      "We treat near-misses as gifts. Every reported near-miss is an opportunity to fix something before it becomes an incident. Our culture rewards reporting, not concealment.",
      "Emergency preparedness is the most under-invested area in our industry. Mock drills, on-site clinics, and pre-positioned response teams are not luxuries; they are non-negotiable for any operator that takes its workforce seriously.",
    ],
  },
];

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Safety", to: "/safety" },
  { label: "Careers", to: "/careers" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];
